import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { FileSpreadsheet } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  PUBLISHER_STATUSES,
  PUBLISHER_TYPES,
  useApi,
  type PublisherDTO,
} from "../../api";
import { PUBLISHER_STATUS_VARIANT, PUBLISHER_TYPE_VARIANT } from "../../components/badges";
import { EnumBadge, percent } from "../../components/cells";
import { crudEditor, numberField, selectField, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * Publishers — the rights holders behind every artwork.
 *
 * `royalty` is stored as a percentage (0–100) but the grid shows it as one, so
 * the display divides by 100 exactly as `FormatUtil.percentFormat` did. A row
 * opens the publisher's page, which holds its metadata template; the fields
 * themselves are edited here, from the row.
 */
export function PublishersPage() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.CONFIG_PUBLISHER);
  const title = t("navigation.item.publishers", "Publishers");

  const { data, isLoading, error } = useQuery({
    queryKey: ["publisher"],
    queryFn: () => api.publishers.findAll(),
    enabled: allowed,
  });

  const rows = data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["publisher"] });

  const cols = useMemo(() => {
    const c = defineColumns<PublisherDTO>();
    return [
      c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 }),
      c.text("code", (row) => row.code, { headerKey: "column.code", priority: 2 }),
      c.custom(
        "type",
        (row) => row.type,
        (row) => <EnumBadge value={row.type} variants={PUBLISHER_TYPE_VARIANT} />,
        { headerKey: "column.type", filter: "select", priority: 3 },
      ),
      c.custom(
        "status",
        (row) => row.status,
        (row) => <EnumBadge value={row.status} variants={PUBLISHER_STATUS_VARIANT} />,
        { headerKey: "column.status", filter: "select", priority: 3 },
      ),
      c.custom("royalty", (row) => row.royalty, (row) => percent(row.royalty), {
        headerKey: "column.royalty",
        filter: "number",
        align: "right",
        priority: 4,
      }),
    ];
  }, []);

  const editor = useMemo(
    () =>
      crudEditor<PublisherDTO>({
        client: api.publishers,
        refresh,
        t,
        fields: {
          name: textField(
            (row) => row.name,
            (row, value) => ({ ...row, name: value }),
            true,
          ),
          // Codes prefix every artwork code, so they are stored uppercase.
          code: textField(
            (row) => row.code,
            (row, value) => ({ ...row, code: value.toUpperCase() }),
            true,
          ),
          type: selectField(
            (row) => row.type,
            (row, value) => ({ ...row, type: value as PublisherDTO["type"] }),
            PUBLISHER_TYPES,
          ),
          status: selectField(
            (row) => row.status,
            (row, value) => ({ ...row, status: value as PublisherDTO["status"] }),
            PUBLISHER_STATUSES,
          ),
          royalty: numberField(
            (row) => row.royalty,
            (row, value) => ({ ...row, royalty: Number(value) }),
            true,
          ),
        },
        newRow: () => ({
          id: 0,
          name: "",
          code: "",
          type: "PERSON",
          status: "ACTIVE",
          royalty: 0,
        }),
        validate: (row) => {
          const errors: Record<string, string> = {};
          const required = t("form.validation.field.required", "Value is required");
          const unique = t("form.validation.field.unique", "Value already exists");
          if (!row.name.trim()) errors.name = required;
          if (!row.code.trim()) errors.code = required;
          if (rows.some((entry) => entry.id !== row.id && entry.name === row.name)) errors.name = unique;
          if (rows.some((entry) => entry.id !== row.id && entry.code === row.code)) errors.code = unique;
          if (row.royalty < 0 || row.royalty > 100) {
            errors.royalty = t("form.validation.field.invalid", "Value is invalid");
          }
          return Object.keys(errors).length > 0 ? errors : null;
        },
      }),
    [api, rows, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<PublisherDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/config/publisher"
        loading={isLoading}
        editor={editor}
        onRowClick={(row) => void navigate({ to: "/config/publisher/$id", params: { id: String(row.id) } })}
        rowActions={(row) => (
          <Button
            size="sm"
            variant="ghost"
            title={t("publisher.template", "Metadata template")}
            className="size-7 p-0"
            icon={<FileSpreadsheet className="size-3.5" />}
            onClick={() => void navigate({ to: "/config/publisher/$id", params: { id: String(row.id) } })}
          />
        )}
      />
    </>
  );
}
